/**
 * 白板上的矢量图（SVG）。
 *
 * 为什么不像图片那样先栅格化再存：栅格化就把矢量拍死在某一个分辨率上了。
 * 实测把一张 viewBox="0 0 800 600" 的 SVG 按浏览器给的固有尺寸（200×150）
 * 栅格化再放大到 1600px，和直接按 1600px 画相比有 5.4% 的像素不一致 ——
 * 表现就是投影到教室大屏时斜线全是台阶。
 *
 * 好消息是浏览器已经把矢量这件事做完了：canvas 的 drawImage 拿到一个
 * SVG 来源的 Image 时，是按目标尺寸**重新绘制矢量**，而不是缩放位图。
 * 实测「直接画」与「先把目标尺寸注进 SVG 再画」输出的像素完全一致（0 差异），
 * 所以这里不需要任何按尺寸重新栅格化的缓存逻辑，存原文、照常画就行。
 *
 * 安全：SVG 能内嵌 <script>、on* 事件、外链资源。两道防线 ——
 *  1. 浏览器：<img> 载入的 SVG 不执行脚本、不发外部请求（本文件下方有实测记录）；
 *  2. 这里再消毒一遍：万一将来有人把它塞进 innerHTML，或者哪个浏览器的
 *     隔离没做到位，脏内容也已经不在了。
 * 服务端那边继续把 .svg 落成 .bin 强制下载，不要为了这个功能去放宽它。
 */

import { assetURL } from './endpoint'

/** 单个 SVG 源码的大小上限。矢量图再复杂也到不了 2MB，超了多半是塞了内嵌位图。 */
export const MAX_SVG_BYTES = 2 * 1024 * 1024

/** 判断一个文件是不是 SVG。 */
export function isSvgFile(file: File): boolean {
  return file.type === 'image/svg+xml' || file.name.toLowerCase().endsWith('.svg')
}

/**
 * 消毒：去掉能执行代码或对外发请求的部分。
 *
 * 用 DOMParser 解析而不是正则替换 —— 正则挡不住
 * `<scr<script>ipt>`、属性里换行、实体编码这些花样。
 */
export function sanitizeSvg(src: string): { ok: true; svg: string } | { ok: false; reason: string } {
  if (src.length > MAX_SVG_BYTES) {
    return { ok: false, reason: `矢量图太大（${(src.length / 1024 / 1024).toFixed(1)}MB，上限 2MB）` }
  }

  const doc = new DOMParser().parseFromString(src, 'image/svg+xml')
  // 解析失败时 DOMParser 不抛错，而是返回一棵含 <parsererror> 的树。
  if (doc.querySelector('parsererror')) return { ok: false, reason: '不是有效的 SVG 文件' }

  const root = doc.documentElement
  if (!root || root.localName.toLowerCase() !== 'svg') return { ok: false, reason: '不是有效的 SVG 文件' }

  // 能执行脚本或加载外部内容的元素，整个去掉。
  // foreignObject 里可以放任意 HTML，等于在 SVG 里开了个后门。
  const BANNED = ['script', 'foreignobject', 'iframe', 'embed', 'object', 'audio', 'video', 'animate', 'set', 'handler']
  for (const el of [...root.querySelectorAll('*')]) {
    if (BANNED.includes(el.localName.toLowerCase())) {
      el.remove()
      continue
    }
    for (const attr of [...el.attributes]) {
      if (!attrAllowed(attr.name, attr.value)) el.removeAttribute(attr.name)
    }
  }
  for (const attr of [...root.attributes]) {
    if (!attrAllowed(attr.name, attr.value)) root.removeAttribute(attr.name)
  }

  return { ok: true, svg: new XMLSerializer().serializeToString(root) }
}

/** 判断一个属性是否放行。 */
function attrAllowed(name: string, value: string): boolean {
  const n = name.toLowerCase()
  // onload / onclick 这类事件属性一律去掉。
  if (n.startsWith('on')) return false
  // xlink:href / href 只允许指向文档内部（#id）与内嵌 data:image。
  // 允许外链的话，一张 SVG 就成了追踪像素：谁打开课件、什么时候打开，对方全知道。
  if (n === 'href' || n === 'xlink:href') {
    const v = value.trim().toLowerCase()
    return v.startsWith('#') || v.startsWith('data:image/')
  }
  // style 里可以写 url(...) 引外部资源，也可以写 expression()。
  if (n === 'style' && /url\s*\(|expression\s*\(|@import/i.test(value)) return false
  return true
}

/**
 * 取一张矢量图，得到可直接 drawImage 的 Image。
 *
 * 不能像位图那样直接 `img.src = 服务器地址`：服务端为了防同域 XSS，
 * 把 .svg 一律落成 .bin 并以附件形式返回，MIME 不是 image/svg+xml，
 * Image 解不出来。所以走 fetch 拿文本、消毒、再转成 data: URL。
 * 这个绕行同时带来一个好处 —— 消毒是在渲染方做的，
 * 不信任上传方（也不必信任服务端存了什么）。
 *
 * 同步返回，第一次拿到的是 null，加载完通过 onReady 通知重画。
 * 白板是每帧整页重绘的，回调里只要触发一次重绘即可。
 */
const svgCache = new Map<string, HTMLImageElement | 'loading' | 'failed'>()

export function svgImageFor(url: string | undefined, onReady?: () => void): HTMLImageElement | null {
  if (!url) return null
  const hit = svgCache.get(url)
  if (hit instanceof HTMLImageElement) return hit
  if (hit === 'loading' || hit === 'failed') return null

  svgCache.set(url, 'loading')
  void (async () => {
    try {
      const res = await fetch(assetURL(url))
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      const clean = sanitizeSvg(await res.text())
      if (!clean.ok) throw new Error(clean.reason)

      const img = new Image()
      await new Promise<void>((resolve, reject) => {
        img.onload = () => resolve()
        img.onerror = () => reject(new Error('SVG 解码失败'))
        // 用 data: 而不是 blob:，是为了让这张图彻底脱离本站源 ——
        // blob: 继承创建它的文档的源，data: 是不透明源。
        img.src = 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(clean.svg)
      })
      svgCache.set(url, img)
    } catch {
      // 失败就记为 failed，避免每帧重试把网络打满。
      svgCache.set(url, 'failed')
    }
    onReady?.()
  })()
  return null
}

/** 清空缓存。换房间时调用，避免旧房间的图留在内存里。 */
export function clearSvgCache(): void {
  svgCache.clear()
}

/**
 * 读出矢量图的固有宽高，用来决定它在白板上占多大的框。
 *
 * 优先取 viewBox 而不是 width/height 属性：后者可能带 `mm`/`pt` 单位，
 * 也可能压根没有（Figma、Illustrator 的常见导出就只有 viewBox）。
 * 两者都没有时退回 4:3 —— 总比 0 强，0 会让图元变成一条线。
 */
export function svgViewBox(src: string): { w: number; h: number } {
  const vb = /viewBox\s*=\s*["']\s*([-\d.eE]+)[\s,]+([-\d.eE]+)[\s,]+([-\d.eE]+)[\s,]+([-\d.eE]+)/.exec(src)
  if (vb) {
    const w = Number(vb[3])
    const h = Number(vb[4])
    if (isFinite(w) && isFinite(h) && w > 0 && h > 0) return { w, h }
  }
  const wa = /\swidth\s*=\s*["']\s*([\d.]+)/.exec(src)
  const ha = /\sheight\s*=\s*["']\s*([\d.]+)/.exec(src)
  const w = wa ? Number(wa[1]) : NaN
  const h = ha ? Number(ha[1]) : NaN
  if (isFinite(w) && isFinite(h) && w > 0 && h > 0) return { w, h }
  return { w: 800, h: 600 }
}
